// Capacity — sub-tabs: Overview, AM Board, Admin (team CRUD).
import { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import { Loader2, Users, ExternalLink } from "lucide-react";
import { Button } from "../../components/ui/button";
import { fetchJson, initialsOf, fmtDateShort, SectionHeader } from "./pccHelpers";
import PccAmBoard from "./PccAmBoard";

export default function PccCapacity() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="space-y-5" data-testid="pcc-capacity">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-light text-white">Capacity & Resources</h2>
          <p className="text-white/55 text-sm">Team allocation + AM board. For drag-and-drop artist scheduling, open Production Planner.</p>
        </div>
        <Button asChild variant="outline" size="sm" className="border-white/15 bg-white/[0.04]">
          <a href="/production-planner" target="_blank" rel="noreferrer">Open Production Planner <ExternalLink className="w-3.5 h-3.5 ml-1.5" /></a>
        </Button>
      </div>

      <div className="flex items-center gap-1 border-b border-white/[0.08]">
        {[{ id: "overview", label: "Overview" }, { id: "amboard", label: "AM Board" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`relative px-4 py-2 text-[11px] uppercase tracking-[0.08em] font-semibold ${tab === t.id ? "text-white" : "text-white/45 hover:text-white/75"}`} data-testid={`pcc-cap-tab-${t.id}`}>
            {t.label}
            {tab === t.id && <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-amber-400 rounded-full" />}
          </button>
        ))}
      </div>

      {tab === "overview" && <CapacityOverview />}
      {tab === "amboard" && <PccAmBoard />}
    </div>
  );
}

function CapacityOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await fetchJson("/planning-command-center/capacity");
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;
  if (error) return <Card className="bg-red-500/[0.06] border-red-500/30 p-4 text-red-200">{error}</Card>;

  const team = data?.team || [];
  const totalCap = team.reduce((s, t) => s + Number(t.weekly_capacity || 0), 0);
  const totalAlloc = team.reduce((s, t) => s + Number(t.allocated_hours || 0), 0);
  const teamUtil = totalCap > 0 ? Math.min(100, Math.round((totalAlloc / totalCap) * 100)) : 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-[#000000] border-white/[0.08] p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Active Team</div>
          <div className="text-2xl font-light text-white mt-1">{team.length}</div>
        </Card>
        <Card className="bg-[#000000] border-white/[0.08] p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Weekly Capacity</div>
          <div className="text-2xl font-light text-white mt-1 tabular-nums">{totalCap.toFixed(0)} hrs</div>
        </Card>
        <Card className="bg-[#000000] border-white/[0.08] p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Allocation</div>
          <div className={`text-2xl font-light tabular-nums mt-1 ${teamUtil > 90 ? "text-red-300" : teamUtil > 70 ? "text-amber-300" : "text-emerald-300"}`}>{teamUtil}%</div>
        </Card>
      </div>

      <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
        <SectionHeader dot="#8FA79B" title={`Team Capacity (${team.length})`} />
        <div className="divide-y divide-white/[0.06]">
          {team.length === 0 ? (
            <div className="px-6 py-10 text-center text-white/40 text-sm">No active team members.</div>
          ) : team.map(m => (
            <div key={m.user_id} className="px-6 py-3 grid items-center gap-3" style={{ gridTemplateColumns: "auto 1fr 280px 80px" }}>
              <div className="w-9 h-9 rounded-full bg-blue-500/25 flex items-center justify-center text-[12px] font-bold text-blue-300">{initialsOf(m.name)}</div>
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{m.name}</div>
                <div className="text-[11px] text-white/45 font-mono">{m.role} · {m.allocated_hours.toFixed(0)} / {m.weekly_capacity.toFixed(0)} hrs</div>
              </div>
              <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
                <div className={`h-full ${m.utilization_pct > 90 ? "bg-red-400" : m.utilization_pct > 70 ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${Math.min(100, m.utilization_pct)}%` }} />
              </div>
              <div className={`text-right text-sm font-mono tabular-nums ${m.utilization_pct > 90 ? "text-red-300" : m.utilization_pct > 70 ? "text-amber-300" : "text-white"}`}>{m.utilization_pct}%</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
